import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Stack,
  Heading,
  Box,
  Button,
  useToast,
  HStack,
  Spacer,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Image,
  VStack,
  IconButton,
  Text,
  useColorModeValue,
  Badge,
  Flex,
  Divider,
  Progress,
  Tooltip,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
  Icon,
  Tag,
  TagLabel,
  TagLeftIcon,
  useClipboard,
  Circle,
  Select
} from '@chakra-ui/react';
import { FiSave, FiUpload, FiX, FiPackage, FiDollarSign, FiClock, FiImage, FiInfo, FiLink, FiEdit3, FiCopy, FiCheck, FiZap, FiSearch } from 'react-icons/fi';
import ProductSelector from '../components/Editor/ProductSelector';
import PricingRules from '../components/Editor/PricingRules';
import TimerSettings from '../components/Editor/TimerSettings';
import PricePreview from '../components/Editor/PricePreview';

// Custom color palette
const colors = {
  primary: {
    50: '#E6F6FF',
    100: '#BAE3FF',
    200: '#7CC4FA',
    300: '#47A3F3',
    400: '#2186EB',
    500: '#0967D2',
    600: '#0552B5',
    700: '#03449E',
    800: '#01337D',
    900: '#002159'
  },
  accent: {
    50: '#FFE8D9',
    100: '#FFD0B5',
    200: '#FFB088',
    300: '#FF9466',
    400: '#F9703E',
    500: '#F35627',
    600: '#DE3A11',
    700: '#C52707',
    800: '#AD1D07',
    900: '#841003'
  }
};

const BundleEditor = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('basic'); // basic, products, pricing, timer
  const [bundle, setBundle] = useState({
    name: '',
    description: '',
    featured_image: '',
    products: [],
    discounts: {
      type: 'percentage',
      value: 0
    },
    timer: {
      active: false,
      start: null,
      end: null,
      messages: {
        default: 'Limited Time Offer! Only {time} left',
        lastDay: 'Hurry! Only {time} remaining',
        lastHour: 'Act Fast! Bundle ends in {time}'
      },
      showCountdown: true,
      styles: {
        message: {
          color: '#1A202C',
          fontSize: 'medium',
          fontWeight: 'normal',
          textAlign: 'center',
        },
        container: {
          backgroundColor: '#FFF9E6',
          borderStyle: 'solid',
          borderColor: '#FFB74D',
          borderWidth: '2px',
          borderRadius: '8px',
          padding: 'medium',
        },
        countdown: {
          color: '#FF5722',
          size: 'large',
          spacing: 'medium',
        },
        theme: 'default'
      }
    }
  });

  // Get bundle ID from URL if editing existing bundle
  const urlParams = new URLSearchParams(window.location.search);
  const bundleId = urlParams.get('id');
  const isDuplicating = urlParams.get('duplicate');

  // Add refs for each section
  const basicInfoRef = useRef(null);
  const productsRef = useRef(null);
  const pricingRef = useRef(null);
  const timerRef = useRef(null);

  // Function to scroll to a section with offset
  const scrollToSection = (ref) => {
    if (ref.current) {
      const yOffset = -100; // Adjust this value to control the scroll position
      const element = ref.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Function to handle step click
  const handleStepClick = (section, ref) => {
    setActiveSection(section);
    setTimeout(() => {
      if (ref.current) {
        const yOffset = -100;
        const element = ref.current;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100); // Small delay to ensure section is rendered
  };

  useEffect(() => {
    if (bundleId && !isDuplicating) {
      fetchBundle();
    } else if (isDuplicating) {
      fetchBundleForDuplication();
    }
  }, [bundleId, isDuplicating]);

  const fetchBundle = async () => {
    try {
      const response = await fetch(`${qbndAdmin.root}qbnd/v1/bundle/${bundleId}`, {
        headers: {
          'X-WP-Nonce': qbndAdmin.nonce
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bundle');
      }

      const data = await response.json();
      console.log('Fetched bundle data:', data); // Debug log

      // Ensure discounts has the correct structure
      const discounts = data.discounts || { type: 'percentage', value: 0 };

      setBundle({
        ...data,
        discounts: {
          type: discounts.type || 'percentage',
          value: discounts.value || 0
        }
      });
    } catch (error) {
      console.error('Error fetching bundle:', error); // Debug log
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const fetchBundleForDuplication = async () => {
    try {
      const response = await fetch(`${qbndAdmin.root}qbnd/v1/bundle/${isDuplicating}`, {
        headers: {
          'X-WP-Nonce': qbndAdmin.nonce
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bundle for duplication');
      }

      const data = await response.json();
      setBundle({
        ...data,
        name: `${data.name} (Copy)`,
        status: 'draft'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${qbndAdmin.root}wp/v2/media`, {
        method: 'POST',
        headers: {
          'X-WP-Nonce': qbndAdmin.nonce
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();
      setBundle({
        ...bundle,
        featured_image: data.source_url
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const handleSave = async () => {
    if (!bundle.name.trim()) {
      toast({
        title: 'Error',
        description: 'Bundle name is required',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${qbndAdmin.root}qbnd/v1/bundle${bundleId ? `/${bundleId}` : ''}`, {
        method: bundleId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': qbndAdmin.nonce
        },
        body: JSON.stringify({
          ...bundle,
          timer: {
            ...bundle.timer,
            active: bundle.timer.active,
            start: bundle.timer.start,
            end: bundle.timer.end,
            messages: bundle.timer.messages,
            position: bundle.timer.position,
            showCountdown: bundle.timer.showCountdown,
            styles: bundle.timer.styles
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save bundle');
      }

      toast({
        title: 'Success',
        description: 'Bundle saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });

      // Redirect to edit page if creating new bundle
      if (!bundleId) {
        const data = await response.json();
        window.location.href = `${qbndAdmin.adminUrl}admin.php?page=quick-bundles-new&id=${data.id}`;
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const getProgress = () => {
    let progress = 0;
    if (bundle.name) progress += 25;
    if (bundle.products.length > 0) progress += 25;
    if (bundle.discounts.value > 0) progress += 25;
    if (bundle.timer.active) progress += 25;
    return progress;
  };

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const accentColor = colors.accent[500];
  const primaryColor = colors.primary[500];

  const NavButton = ({ section, icon, label, isActive }) => (
    <Button
      leftIcon={icon}
      variant={isActive ? 'solid' : 'ghost'}
      colorScheme={isActive ? 'blue' : 'gray'}
      size="lg"
      width="full"
      justifyContent="flex-start"
      onClick={() => setActiveSection(section)}
      _hover={{
        bg: isActive ? 'blue.500' : 'gray.100',
      }}
      transition="all 0.2s"
    >
      {label}
    </Button>
  );

  return (
    <Box minH="100vh" bg={bgColor} py={8}>
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box
            bg={cardBg}
            borderRadius="2xl"
            overflow="hidden"
            position="relative"
          >
            {/* Background Gradient Effect */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bgGradient={`linear(to-br, ${colors.primary[50]}, ${colors.accent[50]})`}
              opacity={0.1}
            />

            {/* Content */}
            <Box position="relative" p={8}>
              {/* Header Content */}
              <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align={{ base: 'stretch', md: 'center' }}
                gap={4}
              >
                <VStack align="start" spacing={2}>
                  <HStack spacing={3}>
                    <Box
                      p={2}
                      bg={`${colors.primary[50]}`}
                      borderRadius="xl"
                      color={primaryColor}
                    >
                      <Icon as={FiPackage} boxSize={6} />
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Heading size="lg" bgGradient={`linear(to-r, ${colors.primary[500]}, ${colors.accent[500]})`} bgClip="text">
                        {bundleId ? 'Edit Bundle' : 'Create New Bundle'}
                      </Heading>
                      <Text color="gray.600" fontSize="sm">
                        {bundleId ? 'Update your existing bundle' : 'Create an attractive product bundle'}
                      </Text>
                    </VStack>
                  </HStack>
                </VStack>

                <Button
                  leftIcon={<FiSave />}
                  colorScheme="blue"
                  size="lg"
                  onClick={handleSave}
                  isLoading={loading}
                  loadingText="Saving..."
                  px={8}
                  bgGradient={`linear(to-r, ${colors.primary[500]}, ${colors.accent[500]})`}
                  _hover={{
                    bgGradient: `linear(to-r, ${colors.primary[600]}, ${colors.accent[600]})`,
                    transform: 'translateY(-2px)',
                    boxShadow: 'lg',
                  }}
                  transition="all 0.2s"
                >
                  Save Bundle
                </Button>
              </Flex>

              {/* Progress and Steps Section */}
              <Box p={6}>
                <VStack spacing={6} align="stretch">
                  {/* Steps Section */}
                  <SimpleGrid columns={4} spacing={4}>
                    {[
                      { num: 1, title: 'Name Bundle', icon: FiPackage, done: !!bundle.name, ref: basicInfoRef, section: 'basic' },
                      { num: 2, title: 'Add Products', icon: FiImage, done: bundle.products.length > 0, ref: productsRef, section: 'products' },
                      { num: 3, title: 'Set Price', icon: FiDollarSign, done: bundle.discounts.value > 0, ref: pricingRef, section: 'pricing' },
                      { num: 4, title: 'Add Timer', icon: FiClock, done: bundle.timer.active, ref: timerRef, section: 'timer' }
                    ].map((step) => (
                      <Box
                        key={step.num}
                        p={4}
                        bg={step.done ? `${colors.primary[50]}` : 'transparent'}
                        borderRadius="xl"
                        borderWidth="2px"
                        borderColor={step.done ? primaryColor : borderColor}
                        transition="all 0.2s"
                        _hover={{ transform: 'translateY(-2px)', boxShadow: 'md', cursor: 'pointer' }}
                        position="relative"
                        overflow="hidden"
                        onClick={() => handleStepClick(step.section, step.ref)}
                      >
                        <HStack spacing={3}>
                          <Circle
                            size="32px"
                            bg={step.done ? primaryColor : `${colors.primary[100]}`}
                            color={step.done ? 'white' : primaryColor}
                          >
                            <Icon as={step.done ? FiCheck : step.icon} boxSize={4} />
                          </Circle>
                          <VStack align="start" spacing={0}>
                            <Text fontSize="xs" color="gray.500">Step {step.num}</Text>
                            <Text fontWeight="bold" color={step.done ? primaryColor : 'gray.700'}>
                              {step.title}
                            </Text>
                          </VStack>
                        </HStack>
                        {step.done && (
                          <Box
                            position="absolute"
                            top={0}
                            right={0}
                            p={1}
                            bg={primaryColor}
                            borderBottomLeftRadius="md"
                          >
                            <Icon as={FiCheck} boxSize={3} color="white" />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </SimpleGrid>

                  {/* Progress Section */}
                  <Box mt={4} p={6} bg="white" borderRadius="xl" boxShadow="sm" borderWidth="1px">
                    <VStack align="stretch" spacing={4}>
                      <HStack justify="space-between">
                        <HStack spacing={2}>
                          <Icon as={FiZap} color={primaryColor} />
                          <Text fontWeight="bold" color="gray.700">Bundle Progress</Text>
                        </HStack>
                        <Badge
                          px={3}
                          py={1}
                          borderRadius="full"
                          colorScheme={getProgress() === 100 ? 'green' : 'blue'}
                          variant="subtle"
                        >
                          {getProgress()}% Complete
                        </Badge>
                      </HStack>
                      <Progress
                        value={getProgress()}
                        size="lg"
                        borderRadius="full"
                        colorScheme={getProgress() === 100 ? 'green' : 'blue'}
                        hasStripe
                        isAnimated
                        bg={useColorModeValue('gray.100', 'gray.700')}
                      />
                    </VStack>
                  </Box>
                </VStack>
              </Box>
            </Box>
          </Box>

          {/* Main content */}
          <Flex gap={6}>
            {/* Navigation */}
            <VStack
              spacing={4}
              align="stretch"
              minW="250px"
              bg={cardBg}
              p={4}
              borderRadius="lg"
              boxShadow="sm"
              border="1px"
              borderColor={borderColor}
            >
              <NavButton
                section="basic"
                icon={<FiPackage />}
                label="Basic Info"
                isActive={activeSection === 'basic'}
              />
              <NavButton
                section="products"
                icon={<FiImage />}
                label="Products"
                isActive={activeSection === 'products'}
              />
              <NavButton
                section="pricing"
                icon={<FiDollarSign />}
                label="Pricing"
                isActive={activeSection === 'pricing'}
              />
              <NavButton
                section="timer"
                icon={<FiClock />}
                label="Timer"
                isActive={activeSection === 'timer'}
              />
            </VStack>

            {/* Content area */}
            <Box
              flex={1}
              bg={cardBg}
              p={6}
              borderRadius="lg"
              boxShadow="sm"
              border="1px"
              borderColor={borderColor}
            >
              {activeSection === 'basic' && (
                <Box ref={basicInfoRef}>
                  <VStack spacing={4} align="stretch">
                    {/* Bundle Name and Description in one card */}
                    <Box
                      bg={useColorModeValue('white', 'gray.800')}
                      p={4}
                      borderRadius="lg"
                      boxShadow="sm"
                      border="1px"
                      borderColor={borderColor}
                    >
                      {/* Bundle Name */}
                      <FormControl mb={4}>
                        <HStack spacing={2} mb={2}>
                          <Icon as={FiPackage} color={primaryColor} />
                          <FormLabel
                            m={0}
                            fontSize="md"
                            fontWeight="medium"
                          >
                            Bundle Name
                          </FormLabel>
                        </HStack>
                        <Input
                          placeholder="e.g., Ultimate Summer Collection"
                          value={bundle.name}
                          onChange={(e) => setBundle({ ...bundle, name: e.target.value })}
                          size="md"
                        />
                      </FormControl>

                      {/* Bundle Description */}
                      <FormControl>
                        <HStack spacing={2} mb={2}>
                          <Icon as={FiEdit3} color={primaryColor} />
                          <FormLabel
                            m={0}
                            fontSize="md"
                            fontWeight="medium"
                          >
                            Description
                          </FormLabel>
                        </HStack>
                        <Textarea
                          placeholder="Describe the value and benefits of your bundle..."
                          value={bundle.description}
                          onChange={(e) => setBundle({ ...bundle, description: e.target.value })}
                          minH="100px"
                          size="md"
                        />
                      </FormControl>
                    </Box>

                    {/* Featured Image */}
                    <Box
                      bg={useColorModeValue('white', 'gray.800')}
                      borderRadius="lg"
                      boxShadow="sm"
                      border="1px"
                      borderColor={borderColor}
                      overflow="hidden"
                    >
                      <HStack p={4} borderBottom="1px" borderColor={borderColor}>
                        <Icon as={FiImage} color={primaryColor} />
                        <Text fontSize="md" fontWeight="medium">Featured Image</Text>
                      </HStack>

                      <Box>
                        {bundle.featured_image ? (
                          <Box position="relative">
                            <Image
                              src={bundle.featured_image}
                              alt="Bundle featured image"
                              width="100%"
                              height="200px"
                              objectFit="cover"
                            />
                            <HStack
                              position="absolute"
                              top={2}
                              right={2}
                              spacing={2}
                            >
                              <IconButton
                                icon={<FiEdit3 />}
                                colorScheme="blue"
                                size="sm"
                                onClick={() => document.getElementById('image-upload').click()}
                              />
                              <IconButton
                                icon={<FiX />}
                                colorScheme="red"
                                size="sm"
                                onClick={() => setBundle({ ...bundle, featured_image: '' })}
                              />
                            </HStack>
                          </Box>
                        ) : (
                          <Button
                            as="label"
                            htmlFor="image-upload"
                            width="100%"
                            height="200px"
                            variant="ghost"
                            cursor="pointer"
                          >
                            <VStack spacing={2}>
                              <Icon as={FiImage} boxSize={6} color={primaryColor} />
                              <Text>Upload Featured Image</Text>
                            </VStack>
                          </Button>
                        )}
                        <input
                          id="image-upload"
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageUpload}
                        />
                      </Box>
                    </Box>
                  </VStack>
                </Box>
              )}

              {activeSection === 'products' && (
                <Box ref={productsRef}>
                  <VStack spacing={4} align="stretch">
                    {/* Product Selection Area */}
                    <Box
                      bg="white"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={borderColor}
                    >
                      <ProductSelector
                        products={bundle.products}
                        onChange={(products) => setBundle({ ...bundle, products })}
                      />
                    </Box>
                  </VStack>
                </Box>
              )}

              {activeSection === 'pricing' && (
                <Box ref={pricingRef}>
                  <VStack spacing={6} align="stretch">
                    <PricingRules
                      discountType={bundle.discounts.type}
                      discountValue={bundle.discounts.value}
                      onChange={({ type, value }) =>
                        setBundle({
                          ...bundle,
                          discounts: { type, value }
                        })
                      }
                    />
                    <PricePreview
                      products={bundle.products || []}
                      discounts={bundle.discounts || { type: 'percentage', value: 0 }}
                    />
                  </VStack>
                </Box>
              )}

              {activeSection === 'timer' && (
                <Box ref={timerRef}>
                  <VStack spacing={6} align="stretch">
                    <TimerSettings
                      active={bundle.timer.active}
                      start={bundle.timer.start}
                      end={bundle.timer.end}
                      messages={bundle.timer.messages}
                      showCountdown={bundle.timer.showCountdown}
                      styles={bundle.timer.styles}
                      onChange={({
                        active,
                        start,
                        end,
                        messages,
                        showCountdown,
                        styles
                      }) => {
                        setBundle({
                          ...bundle,
                          timer: {
                            ...bundle.timer,
                            active,
                            start,
                            end,
                            messages,
                            showCountdown,
                            styles
                          }
                        });
                      }}
                    />
                  </VStack>
                </Box>
              )}
            </Box>
          </Flex>
        </VStack>
      </Container>
    </Box>
  );
};

export default BundleEditor;